/**
 * NodeTemplateApiClient
 * Fetches and updates DataTemplate records for the node template editor.
 *
 * Endpoints:
 *   POST /api/v1/ai/template/data-template/by-type  — list records by TypeID
 *   PUT  /api/v1/ai/template/data-template/{id}     — update ContentData
 */

import { apiConfig, createGuid } from '@flow-studio/api';

export interface DataTemplateRecord {
  dataTemplateID: number;
  templateName: string;
  contentData: string | null;
  dataTemplateTypeID: number;
  displayOrder: number;
  enabled: boolean;
  [key: string]: any;
}

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('authToken');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (apiConfig.tenantId) headers['X-Tenant-ID'] = String(apiConfig.tenantId);
  return headers;
}

function normalizeRecord(raw: any): DataTemplateRecord {
  return {
    ...raw,
    dataTemplateID: raw.DataTemplateID ?? raw.dataTemplateID,
    contentData: raw.ContentData ?? raw.contentData ?? null,
  };
}

export class NodeTemplateApiClient {
  private readonly base: string;

  constructor() {
    this.base = apiConfig.baseUrl.replace(/\/$/, '');
  }

  async fetchRawRecords(typeId: 13 | 14 | 15): Promise<DataTemplateRecord[]> {
    const res = await fetch(`${this.base}/api/v1/ai/template/data-template/by-type`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        metadata: { requestId: createGuid(), timestamp: new Date().toISOString() },
        DataTemplateTypeID: { ID: typeId },
        PageNumber: 1,
        PageSize: 2000,
      }),
    });
    if (res.status === 401 || res.status === 403) {
      throw new Error(`Unauthorized fetching templates (${res.status})`);
    }
    if (!res.ok) {
      throw new Error(`Failed to fetch templates for typeId ${typeId}: HTTP ${res.status}`);
    }
    const json = await res.json();
    const items: any[] = json?.data ?? json?.Data ?? json?.items ?? json?.Items ?? [];
    return Array.isArray(items) ? items.map(normalizeRecord) : [];
  }

  async updateTemplate(
    record: DataTemplateRecord,
    json: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch(
        `${this.base}/api/v1/ai/template/data-template/${record.dataTemplateID}`,
        {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            metadata: { requestId: createGuid(), timestamp: new Date().toISOString() },
            data: { DataTemplateID: record.dataTemplateID, ContentData: json },
            id: { id: record.dataTemplateID },
          }),
        }
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const e = await res.json();
          msg = e?.error?.message ?? e?.message ?? e?.Message ?? msg;
        } catch {}
        return { success: false, error: msg };
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message ?? 'Network error' };
    }
  }
}
